import React, { useRef, useState, useEffect, useMemo } from "react";
import clsx from "clsx";

import { ITeachingMaterial, ITeachingMaterialModalRect } from "../../../state";
import { isOldMaterial } from "../../../libs/teachingMaterialUtils";
import { ReactComponent as LessonCompleteIcon } from "../../../assets/svg/lesson_complete.svg";
import { ReactComponent as LessonIncompleteIcon } from "../../../assets/svg/lesson_incomplete.svg";

import styles from "./TeachingMaterialModal.module.scss";

interface TeachingMaterialModalProps {
  material: ITeachingMaterial;
  targetRect: ITeachingMaterialModalRect;
  targetClassIds: number[];
  onSelectLesson: (lessonId: number) => void;
}

const TeachingMaterialModal: React.FC<TeachingMaterialModalProps> = ({
  material,
  targetRect,
  targetClassIds,
  onSelectLesson,
}) => {
  const lessons = useMemo(
    () =>
      (material.lessons || []).filter(
        l =>
          isOldMaterial(material.mitsuwa_id) || !l.title?.includes("はじめに"),
      ),
    [material.lessons, material.mitsuwa_id],
  );
  const modalRef = useRef<HTMLDivElement>(null);
  const [positionStyle, setPositionStyle] = useState<React.CSSProperties>({
    opacity: 0,
    visibility: "hidden",
  });

  useEffect(() => {
    if (!modalRef.current) return;

    const modalHeight = modalRef.current.offsetHeight;
    const windowHeight = window.innerHeight;
    const GAP = 14;
    const HEADER_OFFSET = 50;

    const left = targetRect.right + GAP;
    let top = targetRect.top;

    if (top + modalHeight > windowHeight) {
      const shiftedTop = targetRect.top - HEADER_OFFSET;
      if (shiftedTop + modalHeight <= windowHeight) {
        top = shiftedTop;
      } else {
        top = targetRect.bottom - modalHeight;
      }
    }

    if (top < 10) top = 10;

    setPositionStyle({ top, left, opacity: 1 });
  }, [targetRect, lessons]);

  const headerTitle = material.title
    ? material.title.replace(/\s*-高校-|\s*-中学-/g, "")
    : "";

  return (
    <div
      ref={modalRef}
      className={styles.container}
      style={positionStyle}
      onClick={e => e.stopPropagation()}
    >
      <div className={styles.header}>
        <span className={styles.titleText}>{headerTitle}</span>
      </div>
      <div className={styles.body}>
        <div className={styles.bodyScroll}>
          {lessons.map(lesson => {
            const isIntro = Boolean(
              lesson.title && lesson.title.includes("はじめに"),
            );
            const showIcon = !isIntro;

            let hasEnoughAnswers = false;
            if (showIcon && lesson.answer_statuses) {
              hasEnoughAnswers = lesson.answer_statuses.some(
                status =>
                  targetClassIds.includes(status.klass_id) &&
                  status.has_enough_answers,
              );
            }

            return (
              <div
                key={lesson.id}
                className={styles.lessonRow}
                onClick={() => onSelectLesson(lesson.id)}
              >
                {showIcon ? (
                  <div className={styles.iconWrapper}>
                    {hasEnoughAnswers ? (
                      <LessonCompleteIcon />
                    ) : (
                      <LessonIncompleteIcon />
                    )}
                  </div>
                ) : (
                  <div className={styles.iconPlaceholder} />
                )}
                <div className={styles.textWrapper}>
                  {isIntro ? (
                    <div
                      className={clsx(
                        styles.lessonTitle,
                        styles.lessonTitleIntro,
                      )}
                    >
                      {lesson.title?.replace(/\s+/g, "")}
                    </div>
                  ) : (
                    <div
                      className={clsx(
                        styles.lessonInfoRow,
                        !lesson.published &&
                          !lesson.teacher_only &&
                          styles.unpublished,
                      )}
                    >
                      {lesson.name?.trim() && (
                        <div className={styles.lessonName}>{lesson.name}</div>
                      )}
                      {lesson.title && (
                        <div className={styles.lessonTitle}>{lesson.title}</div>
                      )}
                    </div>
                  )}
                  {!lesson.published && !lesson.teacher_only && (
                    <div className={styles.unpublishedBadge}>
                      生徒に非公開です
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TeachingMaterialModal;
